import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Button,
  IconButton,
  Typography,
  Collapse,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useParams } from "react-router-dom";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import PipeBuilderResults from "../../pipebuilder/PipeBuilderResults";
import PipeBuilder from "../../pipebuilder/PipeBuilder";

export default function EnhancedPipeBuilderMainMenu({
  value,
  onChange,
  queryTypes,
  isTrigger = false,
}) {
  const { brandId } = useParams();
  const [queryResponse, setQueryResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isQueryBuilderExpanded, setIsQueryBuilderExpanded] = useState(true);
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  const [joinConfigs, setJoinConfigs] = useState({});

  const {
    handleGetQueryResults,
    handdleExecuteEnhancedQuery,
    handleGetDynamicTableFields,
  } = useAutomations();

  const handlePipeBuilderChange = useCallback(
    (newValue) => {
      onChange(newValue);
    },
    [onChange]
  );

  // Load dynamic tables when component mounts
  useEffect(() => {
    if (brandId) {
      handleGetDynamicTableFields(brandId);
    }
  }, [brandId]);

  useEffect(() => {
    console.log(value);
    const initial =
      value.conditions && value.conditions[0] && value.conditions[0].joinConfigs
        ? value.conditions[0].joinConfigs
        : {};
    console.log(initial);
    setJoinConfigs(initial);
  }, [value]);

  const handleRunQuery = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log(joinConfigs);
      const effectiveJoinConfigs =
        joinConfigs && Object.keys(joinConfigs).length
          ? joinConfigs
          : value && value[0] && value[0].joinConfigs
          ? value[0].joinConfigs
          : {};

      console.log(effectiveJoinConfigs);
      const result = await handdleExecuteEnhancedQuery(
        brandId,
        value,
        effectiveJoinConfigs
      ); // ADD joinConfigs HERE
      setQueryResponse(result);
      // Expand results and collapse query builder after running query
      setIsResultsExpanded(true);
      setIsQueryBuilderExpanded(false);
    } catch (error) {
      console.error("Error fetching query:", error);
      setQueryResponse({ error: "Failed to fetch query result" });
      setIsResultsExpanded(true);
      setIsQueryBuilderExpanded(false);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, value, joinConfigs, handdleExecuteEnhancedQuery]); // ADD joinConfigs HERE

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minWidth: "1400px",
        height: "100%",
      }}
    >
      {/* PipeBuilder - Left column with Accordion */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s ease-in-out",
          width: isQueryBuilderExpanded ? "50%" : "48px",
          minWidth: isQueryBuilderExpanded ? "700px" : "48px",
          borderRight: "2px solid",
          borderColor: "divider",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Collapsed State - Vertical Label */}
        {!isQueryBuilderExpanded && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "48px",
              height: "100%",
              bgcolor: "background.default",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            onClick={() => setIsQueryBuilderExpanded(true)}
          >
            <Typography
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontWeight: "medium",
                color: "text.secondary",
                userSelect: "none",
                fontSize: "0.875rem",
              }}
            >
              Query Builder
            </Typography>
          </Box>
        )}

        {/* Expanded State - Full Content */}
        <Collapse
          in={isQueryBuilderExpanded}
          orientation="horizontal"
          sx={{ width: "100%", height: "100%" }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Sticky Header with collapse button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                position: "sticky",
                top: 0,
                left: 0,
                zIndex: 10,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                Query Builder
              </Typography>
            </Box>

            {/* PipeBuilder Content - Scrollable */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <PipeBuilder
                value={value}
                onChange={handlePipeBuilderChange}
                queryTypes={queryTypes}
                onJoinConfigChange={setJoinConfigs}
                isTrigger={isTrigger}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* PipeBuilderResults - Right column */}
      <Box
        sx={{
          flex: 1,
          minWidth: "500px",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header with title and collapse button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "medium" }}>
            Query Results with Join Data
          </Typography>
          {isQueryBuilderExpanded && (
            <IconButton
              onClick={() => setIsQueryBuilderExpanded(false)}
              size="small"
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
          {!isQueryBuilderExpanded && (
            <IconButton
              onClick={() => setIsQueryBuilderExpanded(true)}
              size="small"
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Run Query Button underneath Results */}
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleRunQuery}
            disabled={isLoading || !value || value.length === 0}
            sx={{ minWidth: "120px" }}
          >
            {isLoading ? <CircularProgress size={20} /> : "Run Query"}
          </Button>
        </Box>

        {/* Results Content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <PipeBuilderResults
              value={value}
              result={queryResponse}
              isExpanded={isResultsExpanded}
              setIsExpanded={setIsResultsExpanded}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
